import type { ISwapAggregatorPlugin, ISwapAggregatorPluginRegistry, ISwapAggregatorSettingsStore } from "./interfaces"

/**
 * Plugin Registry
 * Manages aggregator plugin registration and discovery
 */
export class SwapAggregatorPluginRegistry implements ISwapAggregatorPluginRegistry {
  private plugins = new Map<string, ISwapAggregatorPlugin>()

  register(plugin: ISwapAggregatorPlugin): void {
    if (this.plugins.has(plugin.id)) {
      throw new Error(`Plugin with id ${plugin.id} already registered`)
    }
    this.plugins.set(plugin.id, plugin)
  }

  get(id: string): ISwapAggregatorPlugin | undefined {
    return this.plugins.get(id)
  }

  getAll(): ISwapAggregatorPlugin[] {
    return Array.from(this.plugins.values())
  }

  getEnabled(settingsStore: ISwapAggregatorSettingsStore): ISwapAggregatorPlugin[] {
    return this.getAll().filter((plugin) => {
      if (plugin.id === "scale") {
        return plugin.isProductionReady
      }
      const aggregator = settingsStore.SwapAggregators.find(plugin.id)
      if (settingsStore.SwapAggregators.fallbackMode === "multi") {
        return Boolean(aggregator?.enabled) && plugin.isProductionReady
      }
      const isActiveFallback = settingsStore.SwapAggregators.activeFallbackAggregatorId === plugin.id
      return (aggregator?.enabled || isActiveFallback) && plugin.isProductionReady
    })
  }

  enableAggregator(id: string, settingsStore: ISwapAggregatorSettingsStore): void {
    const aggregator = settingsStore.SwapAggregators.find(id)
    if (aggregator) {
      aggregator.setEnabled(true)
    } else {
      throw new Error(`Aggregator with id ${id} not found in settings store`)
    }
  }

  disableAggregator(id: string, settingsStore: ISwapAggregatorSettingsStore): void {
    const aggregator = settingsStore.SwapAggregators.find(id)
    if (aggregator) {
      aggregator.setEnabled(false)
    } else {
      throw new Error(`Aggregator with id ${id} not found in settings store`)
    }
  }

  toggleAggregator(id: string, settingsStore: ISwapAggregatorSettingsStore): void {
    const aggregator = settingsStore.SwapAggregators.find(id)
    if (aggregator) {
      aggregator.setEnabled(!aggregator.enabled)
    } else {
      throw new Error(`Aggregator with id ${id} not found in settings store`)
    }
  }

  isAggregatorEnabled(id: string, settingsStore: ISwapAggregatorSettingsStore): boolean {
    const aggregator = settingsStore.SwapAggregators.find(id)
    if (settingsStore.SwapAggregators.fallbackMode === "multi") {
      return Boolean(aggregator?.enabled)
    }
    return aggregator?.enabled || settingsStore.SwapAggregators.activeFallbackAggregatorId === id || false
  }
}
